import { addDays, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { DatePicker } from 'src/components/DatePicker';
import { DateRangePicker } from 'src/components/DateRangePicker';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Report, ReportDateFilters } from 'src/models/report/_types';
import { formatMonthDayYear } from 'src/utils/dateFormatter';
interface ReportDateFilterProps {
  report: Report;
  field: string;
  label: string;
  value?: ReportDateFilters;
  setValue: (newVal: ReportDateFilters) => void;
  valueOnly?: boolean;
}

export function ReportDateFilter({
  report,
  label,
  value,
  setValue,
  valueOnly,
}: ReportDateFilterProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'filters.reportDate',
  });
  const showDateFilters = report.filters.some((filter) => filter.id === 'date');
  const showFutureDateFilters = report.filters.some(
    (filter) => filter.id === 'futureDate'
  );
  const showPeriodFilters = report.filters.some(
    (filter) => filter.id === 'time_period'
  );
  const showUtcFilter = report.filters.some((filter) => filter.id === 'utc');
  const showDateCondition = report.filters.some(
    (filter) => filter.id === 'date_filter'
  );
  const singleDateFilter = report.filters.some(
    (filter) => filter.id === 'single_date'
  );

  const dateConditions =
    report.reportType === 'total_visits_per_client'
      ? [
          { value: 'session_time', name: t('dateFiltersBySessionTime') },
          { value: 'time_booked', name: t('dateFiltersByTimeBooked') },
        ]
      : [
          {
            value: 'packages_given_during_date_range',
            name: t('packagesGivenDuringDateRange'),
          },
          {
            value: 'packages_active_during_date_range',
            name: t('packagesActiveDuringRange'),
          },
        ];

  const handleUtcChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue({ ...value!, utc: event.target.checked });
  };

  if (valueOnly) {
    return (
      <>
        {singleDateFilter && value?.date ? (
          formatMonthDayYear(value.date)
        ) : (
          <>
            {value?.startDate ? formatMonthDayYear(value.startDate) : ''}
            {value?.endDate
              ? ` ${t('to')} ${formatMonthDayYear(value.endDate)}`
              : ''}
          </>
        )}
      </>
    );
  }

  return (
    <Stack spacing={3}>
      {(showDateFilters ||
        showFutureDateFilters ||
        showPeriodFilters ||
        singleDateFilter) && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">
              {singleDateFilter ? t('selectDate') : t('selectDates')}
            </Typography>
            {report.dateDescription && (
              <Box>
                <HtmlTypography
                  variant="caption"
                  color="text.secondary"
                  linkifyOptions={{ nl2br: false }}
                >
                  {report.dateDescription}
                </HtmlTypography>
              </Box>
            )}
          </Box>
          {singleDateFilter ? (
            <DatePicker
              value={value?.date ? new Date(value.date) : null}
              onChange={(newDate) => {
                if (newDate instanceof Date && isValid(newDate)) {
                  const dateString = newDate.toISOString();
                  setValue({
                    ...value!,
                    date: dateString,
                    startDate: dateString,
                    endDate: dateString,
                  });
                }
              }}
              sx={{ width: 200 }}
              label=""
            />
          ) : (
            <Box>
              <DateRangePicker
                label={label}
                multiInput
                allTime={['lifetime_spend'].includes(report.reportType)}
                value={[
                  value?.startDate ? new Date(value.startDate) : null,
                  value?.endDate ? new Date(value.endDate) : null,
                ]}
                minDate={
                  showFutureDateFilters ? addDays(new Date(), 1) : undefined
                }
                onChange={(newValue) => {
                  if (
                    newValue[0] instanceof Date &&
                    isValid(newValue[0]) &&
                    newValue[1] instanceof Date &&
                    isValid(newValue[1])
                  ) {
                    const start = newValue[0].toISOString();
                    const end = newValue[1].toISOString();
                    setValue({
                      ...value!,
                      startDate: start,
                      endDate: end,
                    });
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {showDateCondition && (
        <Autocomplete
          options={dateConditions}
          getOptionLabel={(option) => option.name}
          value={
            dateConditions.find((c) => c.value === value?.dateFilter) || null
          }
          onChange={(_, newValue) => {
            if (newValue) {
              setValue({
                ...value!,
                dateFilter: newValue.value,
              });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('selectDateFilter')} />
          )}
        />
      )}

      {showUtcFilter && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox checked={value?.utc} onChange={handleUtcChange} />
          <Typography>{t('useUtcDates')}</Typography>
          <Tooltip title={t('utcTooltip')}>
            <Typography color="action">{t('infoIcon')}</Typography>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
}
